'use client';

import React, { useState, useEffect } from 'react';
import { apiClient } from '@/app/api/client';
import { apiConfig } from '@/config/api';
import { Button } from '@/components/common/Button';
import Select, { SelectOption } from '@/components/common/Select';
import { Loader2, Sparkles, RefreshCw, Check } from 'lucide-react';
import { ImageCropPreview } from './ImageCropPreview';
import { useToast } from '@/hooks/useToast';

// ============================================
// 🚧 DEV MODE - SET TO false BEFORE PRODUCTION
// ============================================
const DEV_MODE = false; // Toggle this to enable/disable mock image generation
const DEV_TEST_IMAGE = '/test.png'; // Path to test image in public folder
// ============================================

// Types
interface ImageContext {
  articleId?: string;
  articleTitle: string;
  currentParagraph: string;
  selectedText?: string;
  cursorPosition?: number;
  scenario?: 'hero' | 'contextual';
}

interface SelectedImage {
  url: string;
  alt: string;
  assetId?: string;
}

interface TempImage {
  tempId: string;
  imageData: string;
  mimeType: string;
  size: number;
  metadata: any;
}

interface ImageGenerationFormProps {
  context: ImageContext;
  userMessage?: string;
  onGenerated: (image: SelectedImage) => void;
  onClose: () => void;
  onBack: () => void;
  isGenerating: boolean;
  onIsGeneratingChange: (isGenerating: boolean) => void;
}

export const ImageGenerationForm: React.FC<ImageGenerationFormProps> = ({
  context,
  userMessage,
  onGenerated,
  onClose,
  onBack,
  isGenerating,
  onIsGeneratingChange
}) => {
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState<string>('illustration');
  const [aspectRatio, setAspectRatio] = useState<string>('16:9');
  // isGenerating state is now controlled by parent
  const [isConfirming, setIsConfirming] = useState(false);
  const [tempImage, setTempImage] = useState<TempImage | null>(null);
  const { addToast } = useToast();
  const [isPreviewFullscreen, setIsPreviewFullscreen] = useState(false);
  const [hasLogo, setHasLogo] = useState(false);
  const [includeLogo, setIncludeLogo] = useState(false);

  useEffect(() => {
    const checkLogo = async () => {
      try {
        const response = await apiClient.get('/api/tenant/settings');
        if (response.data?.logoUrl) {
          setHasLogo(true);
          setIncludeLogo(true); // Default to true if logo exists
        }
      } catch (error) {
        console.error('Failed to check tenant logo:', error);
      }
    };
    checkLogo();
  }, []);


  const styles: SelectOption<string>[] = [
    { value: 'illustration', label: 'Illustration', description: 'Artistic, colorful drawings perfect for blog posts' },
    { value: 'photo', label: 'Photo', description: 'Realistic photographic style for professional content' },
    { value: 'diagram', label: 'Diagram', description: 'Technical diagrams and infographics' },
    { value: 'abstract', label: 'Abstract', description: 'Modern abstract art for creative topics' },
  ];

  const aspectRatios = [
    { value: '21:9', label: '21:9', description: 'Ultra-wide banner' },
    { value: '16:9', label: '16:9', description: 'Widescreen' },
    { value: '5:4', label: '5:4', description: 'Nearly square' },
    { value: '4:3', label: '4:3', description: 'Standard' },
    { value: '3:2', label: '3:2', description: 'Classic photo' },
    { value: '1:1', label: '1:1', description: 'Square' },
    { value: '2:3', label: '2:3', description: 'Portrait photo' },
    { value: '3:4', label: '3:4', description: 'Tall portrait' },
    { value: '4:5', label: '4:5', description: 'Social media' },
    { value: '9:16', label: '9:16', description: 'Mobile portrait' },
  ];

  // Generate AI-suggested prompt from context
  useEffect(() => {
    const suggestedPrompt = generatePromptFromContext();
    setPrompt(suggestedPrompt);
  }, [context, style]);

  const generatePromptFromContext = () => {
    // Leave empty by default - user can add specific instructions if needed
    // The backend will use article context automatically for better results
    return '';
  };

  const handleGenerate = async () => {
    // Prompt is optional - backend will use article context if empty
    onIsGeneratingChange(true);

    try {
      // 🚧 DEV MODE: Return mock image immediately
      if (DEV_MODE) {
        // Fetch test image and convert to base64
        const response = await fetch(DEV_TEST_IMAGE);
        const blob = await response.blob();
        const reader = new FileReader();

        reader.onloadend = () => {
          const base64data = reader.result as string;
          // Remove the data URL prefix to get just the base64 string
          const base64String = base64data.split(',')[1];

          setTempImage({
            tempId: 'mock-temp-id',
            imageData: base64String,
            mimeType: blob.type,
            size: blob.size,
            metadata: {
              prompt: prompt.trim(),
              style,
              aspectRatio,
              generatedAt: new Date().toISOString()
            }
          });
          onIsGeneratingChange(false);
        };

        reader.readAsDataURL(blob);
        return;
      }

      // Production: Call real API
      const response = await apiClient.post('/api/gen/generate-image', {
        prompt: prompt.trim(),
        style,
        aspectRatio,
        context: {
          articleId: context.articleId, // Pass articleId for fetching full content
          articleTitle: context.articleTitle,
          currentParagraph: context.currentParagraph,
          selectedText: context.selectedText,
        },
        userMessage: userMessage || undefined,
        includeLogo: hasLogo && includeLogo,
      });

      setTempImage(response.data.tempImage);
    } catch (err: any) {
      addToast(err.response?.data?.details || err.message || 'Failed to generate image', 'error');
    } finally {
      onIsGeneratingChange(false);
    }
  };

  const handleConfirm = async () => {
    if (!tempImage) return;

    setIsConfirming(true);

    try {
      // Send image data back to backend for saving
      const response = await apiClient.post('/api/user/temp-images/confirm', {
        imageData: tempImage.imageData,
        mimeType: tempImage.mimeType,
        size: tempImage.size,
        metadata: tempImage.metadata,
      });

      const asset = response.data.asset;

      // Call onGenerated with the confirmed asset
      onGenerated({
        url: apiConfig.getUrl(asset.url),
        alt: asset.meta?.alt || asset.meta?.filename || 'AI-generated image',
        assetId: asset.uuid,
      });

      // Close dialog
      onClose();
    } catch (err: any) {
      addToast(err.response?.data?.details || err.message || 'Failed to save image', 'error');
    } finally {
      setIsConfirming(false);
    }
  };

  const handleRegenerate = () => {
    setTempImage(null);
    setTimeout(() => handleGenerate(), 100);
  };

  const handleDiscard = () => {
    setTempImage(null);
  };

  const handleApplyCrop = (croppedImageData: string, croppedSize: number) => {
    if (!tempImage) return;
    // Update tempImage with cropped data
    setTempImage({
      ...tempImage,
      imageData: croppedImageData,
      size: croppedSize
    });
  };

  return (
    <div
      className="space-y-4"
      style={isPreviewFullscreen ? { minHeight: '800px' } : undefined}
    >
      {/* Two Column Layout: 3/5 left, 2/5 right */}
      {!tempImage && (
        <div className="grid grid-cols-[3fr_2fr] gap-6">
          {/* Left Column: Aspect Ratio, Style, Instructions */}
          <div className="flex flex-col space-y-4">
            {/* Aspect Ratio - At Top */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Aspect Ratio</label>
              <div className="grid grid-cols-5 gap-2">
                {aspectRatios.map((ratio) => (
                  <button
                    key={ratio.value}
                    onClick={() => setAspectRatio(ratio.value)}
                    disabled={isGenerating}
                    title={ratio.description}
                    className={`px-2 py-1.5 rounded text-xs font-medium transition-colors ${aspectRatio === ratio.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                  >
                    {ratio.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {aspectRatios.find(r => r.value === aspectRatio)?.description || 'Select aspect ratio'}
              </p>
            </div>

            {/* Style */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Style</label>
              <Select
                value={style}
                onChange={setStyle}
                options={styles}
                disabled={isGenerating}
              />
            </div>

            {/* Business Logo Option */}
            {hasLogo && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="includeLogo"
                  checked={includeLogo}
                  onChange={(e) => setIncludeLogo(e.target.checked)}
                  disabled={isGenerating}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="includeLogo" className="text-sm text-gray-700 select-none">
                  Include Business Logo
                </label>
              </div>
            )}


            {/* Generation Instructions - Takes remaining space */}
            <div className="flex-1 flex flex-col min-h-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Generation Instructions
              </label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Optional: Add specific instructions like 'minimalist style', 'vibrant colors', 'technical diagram', etc."
                className="flex-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none min-h-[120px] text-gray-900"
                disabled={isGenerating}
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty to let AI generate based on your article context, or add specific style/content requirements.
              </p>
            </div>
          </div>

          {/* Right Column: Preview - Fixed size 320x400 */}
          <div className="flex flex-col">
            <label className="block text-sm font-medium text-gray-700 mb-1">Preview in Article</label>
            <div className="w-[320px] h-[400px] bg-white border-2 border-gray-200 rounded-lg p-3 shadow-sm overflow-hidden">
              {/* Mock article page - scales to fit */}
              <div className="h-full flex flex-col">
                {/* Article title */}
                <div className="h-2 bg-gray-300 rounded w-3/4 mb-2 flex-shrink-0"></div>

                {/* Image placeholder - Scales to fit available space */}
                <div className="flex-1 flex items-center justify-center min-h-0 py-2">
                  <div
                    className="bg-gradient-to-br from-blue-100 to-blue-200 border-2 border-blue-300 rounded-md"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: (() => {
                        const [w, h] = aspectRatio.split(':').map(Number);
                        const ratio = w / h;
                        // If portrait (ratio < 1), limit by height; if landscape, limit by width
                        return ratio < 1 ? 'auto' : '100%';
                      })(),
                      height: (() => {
                        const [w, h] = aspectRatio.split(':').map(Number);
                        const ratio = w / h;
                        // If portrait (ratio < 1), limit by height; if landscape, limit by width
                        return ratio < 1 ? '100%' : 'auto';
                      })(),
                      aspectRatio: (() => {
                        const [w, h] = aspectRatio.split(':').map(Number);
                        return `${w} / ${h}`;
                      })()
                    }}
                  />
                </div>

                {/* Mock text lines */}
                <div className="space-y-1.5 flex-shrink-0 mt-2">
                  <div className="h-1.5 bg-gray-200 rounded"></div>
                  <div className="h-1.5 bg-gray-200 rounded w-5/6"></div>
                  <div className="h-1.5 bg-gray-200 rounded w-4/6"></div>
                </div>

                {/* Ratio label */}
                <div className="text-center text-xs text-gray-500 mt-2 flex-shrink-0 font-medium">{aspectRatio}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer with Generate Button */}
      {!tempImage && (
        <div className="flex justify-between items-center pt-4 border-t">
          <Button variant="outline" onClick={onBack}>
            ← Back
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={isGenerating}
            size="sm"
            className="flex items-center gap-2"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Generate Image
              </>
            )}
          </Button>
        </div>
      )}



      {/* Preview Generated Image */}
      {tempImage && (
        <div className="space-y-4">
          <ImageCropPreview
            imageData={tempImage.imageData}
            mimeType={tempImage.mimeType}
            size={tempImage.size}
            height={400}
            onApplyCrop={handleApplyCrop}
            onFullscreenChange={setIsPreviewFullscreen}
          />

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              onClick={handleRegenerate}
              disabled={isConfirming}
              variant="outline"
              size="sm"
              className="flex-1 flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-3 h-3" />
              Regenerate
            </Button>
            <Button
              onClick={handleDiscard}
              disabled={isConfirming}
              variant="outline"
              size="sm"
              className="px-4"
            >
              ✗ Discard
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={isConfirming}
              size="sm"
              className="flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700"
            >
              {isConfirming ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-3 h-3" />
                  Use This Image
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
